import { AgentAvailability } from 'models/agent_status';
import qconsole from 'scripts/lib/qconsole';
import RoutingQueueItem, { RoutingStatus } from 'models/routing_queue_item';
import SoftwareVersionService from 'actions/version/software_version_service';

export const MIN_DELAY_MS = 30 * 1000;
export const MAX_DELAY_MS = 5 * 60 * 1000;
export const RECHECK_INTERVAL_MS = 60 * 1000;
export const INACTIVITY_THRESHOLD_MS = 3 * 60 * 1000;
export const FALLBACK_NOTIFICATION_DELAY_MS = 60 * 60 * 1000;

const INTERACTION_EVENTS = ['mousemove', 'wheel', 'keydown', 'click'];

export default class AutoUpgradeScheduler {
  constructor(context, { onFallback } = {}) {
    this.context = context;
    this.onFallback = onFallback;
    this.pending = false;
    this.ready = false;
    this.lastInteractionAt = null;
    this.timeoutId = null;
    this.recheckIntervalId = null;
    this.fallbackTimeoutId = null;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleInteraction = this.handleInteraction.bind(this);
  }

  schedule() {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.handleInteraction();
    INTERACTION_EVENTS.forEach(name => document.addEventListener(name, this.handleInteraction, { passive: true }));

    const delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
    qconsole.log(`Auto-upgrade scheduled in ${Math.round(delay / 1000)}s`);

    this.timeoutId = setTimeout(() => {
      this.timeoutId = null;
      this.ready = true;
      this.startListening();
      this.attemptUpgrade();
    }, delay);

    this.scheduleFallback();
  }

  scheduleFallback() {
    if (!this.onFallback) {
      return;
    }

    this.fallbackTimeoutId = setTimeout(() => {
      this.fallbackTimeoutId = null;
      if (this.pending) {
        qconsole.log('Auto-upgrade still pending after fallback delay, notifying of optional upgrade');
        this.onFallback();
      }
    }, FALLBACK_NOTIFICATION_DELAY_MS);
  }

  cancel() {
    if (!this.pending) {
      return;
    }

    this.stop();
  }

  attemptUpgrade() {
    if (!this.pending || !this.ready || !this.isAgentIdle()) {
      return;
    }

    qconsole.log('Agent is idle, performing auto-upgrade');
    this.stop();
    SoftwareVersionService.upgrade();
  }

  stop() {
    this.pending = false;
    this.ready = false;
    this.clearTimers();
    this.stopListening();
  }

  handleVisibilityChange() {
    this.attemptUpgrade();
  }

  handleInteraction() {
    this.lastInteractionAt = Date.now();
  }

  startListening() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.recheckIntervalId = setInterval(() => this.attemptUpgrade(), RECHECK_INTERVAL_MS);
  }

  stopListening() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    INTERACTION_EVENTS.forEach(name => document.removeEventListener(name, this.handleInteraction));
    if (this.recheckIntervalId) {
      clearInterval(this.recheckIntervalId);
      this.recheckIntervalId = null;
    }
  }

  clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.fallbackTimeoutId) {
      clearTimeout(this.fallbackTimeoutId);
      this.fallbackTimeoutId = null;
    }
  }

  isAgentIdle() {
    if (this.isAgentBusy()) {
      return false;
    }

    return this.isDocumentHidden() || this.isAgentAway() || this.isAgentInactive();
  }

  // Wrapped so specs can control visibility.
  isDocumentHidden() {
    return document.hidden;
  }

  isAgentAway() {
    const agentStatus = this.context.stores.agentStatus.get();
    return !!agentStatus && agentStatus.getAvailability() === AgentAvailability.AWAY;
  }

  isAgentInactive() {
    return Date.now() - this.lastInteractionAt >= INACTIVITY_THRESHOLD_MS;
  }

  isAgentBusy() {
    const { stores } = this.context;

    const activeCall = stores.activeCall.get();
    if (activeCall && activeCall.conversationId) {
      return true;
    }

    // A non-empty draft makes FlushUnsavedCompositions block the reload with a "Leave site?" prompt.
    if (stores.localCompositions.getAll().some(composition => !composition.isEmpty())) {
      return true;
    }

    return stores.activeSessions.findAll().some(isLiveSession);
  }
}

function isLiveSession(session) {
  return session.queueItem.status === RoutingStatus.ACTIVE || RoutingQueueItem.isOffered(session.queueItem);
}
